/**
 * SAFooterCards — 3 footer cards for Superadmin cockpit
 * Pattern: CockpitPage.js footer cards
 */
import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GlassCard from '../shared/GlassCard';
import { colors } from '../../styles/colors';

const tierStyles = {
  premium: { bgcolor: colors.infoBg, color: colors.info },
  pro: { bgcolor: colors.primaryBg, color: colors.primary },
  essentiel: { bgcolor: colors.neutralBg, color: colors.textMed },
};

const activityIcons = {
  report_sent: DescriptionIcon,
  brand_created: AddIcon,
  crisis_triggered: WarningAmberIcon,
  payment_received: AttachMoneyIcon,
};

function CardTitle({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Playfair Display", Georgia, serif',
        fontSize: '1.2rem',
        fontWeight: 700,
        color: colors.charcoal,
        mb: 2.5,
      }}
    >
      {children}
    </Typography>
  );
}

// --- Card 1: Facturation ---
function BillingCard({ billing }) {
  if (!billing) return null;

  return (
    <GlassCard sx={{ p: 4 }}>
      <CardTitle>Facturation</CardTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Breakdown by tier */}
        {billing.breakdown && (
          <Box sx={{ mb: 1 }}>
            {Object.entries(billing.breakdown).map(([tier, amount]) => {
              const tStyle = tierStyles[tier] || tierStyles.essentiel;
              return (
                <Box key={tier} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={tier.charAt(0).toUpperCase() + tier.slice(1)}
                      size="small"
                      sx={{
                        fontFamily: '"DM Sans", sans-serif', fontSize: '0.68rem', fontWeight: 600,
                        color: tStyle.color, bgcolor: tStyle.bgcolor, borderRadius: '100px', height: 22,
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', fontWeight: 600, color: colors.charcoal }}>
                    {Number(amount).toLocaleString('fr-FR')} EUR
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Overdue */}
        {billing.overdue && billing.overdue.length > 0 && (
          <Box sx={{ borderTop: `1px solid ${colors.borderLight}`, pt: 1 }}>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.danger, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
              En retard
            </Typography>
            {billing.overdue.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textMed }}>
                  {item.agency}
                </Typography>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', fontWeight: 600, color: colors.danger }}>
                  {Number(item.amount).toLocaleString('fr-FR')} EUR
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Upcoming */}
        {billing.upcoming && billing.upcoming.length > 0 && (
          <Box sx={{ borderTop: `1px solid ${colors.borderLight}`, pt: 1 }}>
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textMed, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
              A venir
            </Typography>
            {billing.upcoming.slice(0, 5).map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textMed }}>
                  {item.agency}
                </Typography>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', fontWeight: 600, color: colors.charcoal }}>
                  {Number(item.amount).toLocaleString('fr-FR')} EUR
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </GlassCard>
  );
}

// --- Card 2: Activity ---
function ActivityCard({ activity }) {
  return (
    <GlassCard sx={{ p: 4 }}>
      <CardTitle>Activite recente</CardTitle>
      {activity && activity.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {activity.map((entry, i) => {
            const IconComp = activityIcons[entry.type] || DescriptionIcon;
            const dateStr = entry.date
              ? new Date(entry.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
              : '';
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28, height: 28, borderRadius: '8px',
                    bgcolor: colors.primaryBg, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25,
                  }}
                >
                  <IconComp sx={{ fontSize: 14, color: colors.primary }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal, lineHeight: 1.3 }}>
                    {entry.label}
                  </Typography>
                  <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', color: colors.textLight }}>
                    {dateStr}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.textLight }}>
          Aucune activite recente
        </Typography>
      )}
    </GlassCard>
  );
}

// --- Card 3: Alerts ---
function AlertsCard({ alerts }) {
  const hasAlerts = alerts && (
    (alerts.overdue_payments && alerts.overdue_payments.length > 0) ||
    (alerts.stale_crises && alerts.stale_crises.length > 0) ||
    alerts.ai_cost_warning
  );

  return (
    <GlassCard sx={{ p: 4 }}>
      <CardTitle>Alertes plateforme</CardTitle>
      {hasAlerts ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Overdue payments */}
          {alerts.overdue_payments && alerts.overdue_payments.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal, fontWeight: 600 }}>
                  Paiements en retard
                </Typography>
                <Chip
                  label={alerts.overdue_payments.length}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem', fontWeight: 600,
                    borderRadius: '100px', bgcolor: colors.dangerBg, color: colors.danger,
                    fontFamily: '"DM Sans", sans-serif',
                  }}
                />
              </Box>
              {alerts.overdue_payments.map((p, i) => (
                <Typography key={i} sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textMed, ml: 1 }}>
                  {p.agency} — {Number(p.amount).toLocaleString('fr-FR')} EUR
                </Typography>
              ))}
            </Box>
          )}

          {/* Stale crises */}
          {alerts.stale_crises && alerts.stale_crises.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal, fontWeight: 600 }}>
                  Crises non resolues (+48h)
                </Typography>
                <Chip
                  label={alerts.stale_crises.length}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem', fontWeight: 600,
                    borderRadius: '100px', bgcolor: colors.dangerBg, color: colors.danger,
                    fontFamily: '"DM Sans", sans-serif',
                  }}
                />
              </Box>
              {alerts.stale_crises.map((c, i) => (
                <Typography key={i} sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.78rem', color: colors.textMed, ml: 1 }}>
                  {c.brand}
                </Typography>
              ))}
            </Box>
          )}

          {/* AI cost warning */}
          {alerts.ai_cost_warning && (
            <Box sx={{ bgcolor: colors.warningBg, borderRadius: '12px', p: 1.5 }}>
              <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.warningDark, fontWeight: 600 }}>
                Cout IA eleve — depasse 10% du MRR
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.88rem', color: colors.success }}>
          Aucune alerte
        </Typography>
      )}
    </GlassCard>
  );
}

// --- Main ---
function SAFooterCards({ footer }) {
  if (!footer) return null;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mt: 4 }}>
      <BillingCard billing={footer.billing} />
      <ActivityCard activity={footer.activity} />
      <AlertsCard alerts={footer.alerts} />
    </Box>
  );
}

export default SAFooterCards;
